import type {
  MigrationManifest,
  PersistConfig,
  PersistedState
} from "redux-persist";
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import { AsyncStorage } from "@/store/tabStore/types";

import { clearOtherTabStorages } from "./tabStore";

import { MIGRATION_KEYS } from "./migrations/constant";
import {
  analysisConfigSnapshotReducer,
  clearAnalysisConfigSnapshot
} from "./slice/analysisConfigSnapshotSlice";
import { analysisJobReducer, resetAnalysisJob } from "./slice/analysisJob";
import {
  analysisSummaryReducer,
  resetAnalysisSummaryState
} from "./slice/analysisSummary";
import { authReducer, resetAuth } from "./slice/authSlice";
import type { DatasetState } from "./slice/datasetSlice";
import { datasetReducer, resetDatasetState } from "./slice/datasetSlice";
import {
  globalFilterReducer,
  GlobalFilterState,
  resetGlobalFilter
} from "./slice/globalFilterSlice";
import { mapReducer, resetMapState } from "./slice/mapSlice";
import {
  onboardingReducer,
  resetOnboardingState
} from "./slice/onboardingSlice";
import { resetSpatialState, spatialReducer } from "./slice/spatialSlice";
import { resetUIDataConfig, uiReducer } from "./slice/uiSlice";
import { resetWidgetState, widgetReducer } from "./slice/widgetSlice";
import { sharedStorage, tabStorage } from "./tabStore/storage";

type MigrationManifestTyped<S> = {
  [version: number]: (
    state: (S & PersistedState) | undefined
  ) => S | undefined | Promise<S | undefined>;
};

/** The KEY_PREFIX of redux-persist. Its types have it, but it is not exported. */
const PERSIST_KEY_PREFIX = "persist:";

/**
 * Reads the persisted state in the old serialized format and in the new
 * `serialize: false` format. Thus the state stays after the migration.
 *
 * The type of the stored object shows the format:
 * - Old format: the stored object and its values are JSON strings.
 * - New format: the values are parsed. Do not parse them again, because a
 *   correct string value, for example `analysisId`, is not always valid JSON.
 */
export const getStoredStateCompat = async <S>(
  config: PersistConfig<S>
): Promise<PersistedState> => {
  const raw = await config.storage.getItem(
    `${PERSIST_KEY_PREFIX}${config.key}`
  );

  if (!raw) return undefined;

  const isLegacyFormat = typeof raw === "string";
  const envelope = (isLegacyFormat ? JSON.parse(raw) : raw) as Record<
    string,
    unknown
  >;

  return Object.fromEntries(
    Object.entries(envelope).map(([sliceKey, value]) => [
      sliceKey,
      isLegacyFormat ? JSON.parse(value as string) : value
    ])
  ) as PersistedState;
};

type CreatePersistConfigParams<S> = {
  key: string;
  version: number;
  storage: AsyncStorage;
  migrations?: MigrationManifestTyped<S>;
  whitelist?: string[];
  blacklist?: string[];
  /**
   * The rehydrate timeout of redux-persist. Use 0 for the slices of one tab. The
   * reads of these slices wait for the readiness gate. The default value of 5 s
   * can occur during the migration and rehydrate an empty state. Then the data of
   * the tab is lost.
   */
  timeout?: number;
};

export const createPersistConfig = <S>({
  key,
  version,
  storage,
  migrations = {},
  whitelist,
  blacklist,
  timeout
}: CreatePersistConfigParams<S>) => {
  const baseMigrate = createMigrate(migrations as MigrationManifest, {
    debug: import.meta.env.MODE === "development"
  });

  const migrate = async (state: PersistedState, currentVersion: number) => {
    const fromVersion = state?._persist?.version ?? 0;

    if (fromVersion >= currentVersion) {
      return state;
    }

    return baseMigrate(state, currentVersion);
  };

  return {
    key,
    version,
    storage,
    whitelist,
    blacklist,
    timeout,
    /**
     * IndexedDB stores structured data. Thus JSON serialization adds unnecessary
     * work and memory. Structured cloning also keeps values such as `undefined`,
     * `NaN` and `Date`.
     */
    serialize: false,

    /**
     * Reads a state in the old or in the new storage format. Thus a change of
     * `serialize` does not clear the state. See getStoredStateCompat.
     */
    getStoredState: getStoredStateCompat,
    migrate: Object.keys(migrations).length ? migrate : undefined
  };
};

const mapPersistConfig = createPersistConfig({
  key: "map",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: ["selectedFeature"]
});

const spatialPersistConfig = createPersistConfig({
  key: "spatial",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: [
    "catchmentSettingsTemp",
    "adminAreaSettingsDataTemp",
    "adminTreeSettingsTemp"
  ]
});

const datasetPersistConfig = createPersistConfig<DatasetState>({
  key: "dataset",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const analysisSummaryPersistConfig = createPersistConfig({
  key: "analysisSummary",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const uiPersistConfig = createPersistConfig({
  key: "ui",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: [
    "showPolygonPopup",
    "allowPinMarker",
    "showAreaAdminPopup",
    "showCatchmentPopup",
    "showDatasetPopup",
    "showDataWeightSettingsPopup",
    "showSpatialAnalysisResultTabularModal",
    "sideBarMenuTypePopupDisplay",
    "isExpandLegendVisualization",
    "showProjectList",
    "showLegendInfo",
    "sidebarUserSettingPopupDisplay",
    "showSearchLocationPopup",
    "markerInfo",
    "showGlobalFilterPopup",
    "showLoadingOverlay"
  ]
});

const authPersistConfig = createPersistConfig({
  key: "auth",
  version: 0,
  storage: sharedStorage
});

const analysisJobPersistConfig = createPersistConfig({
  key: "analysisJob",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const globalFilterPersistConfig = createPersistConfig({
  key: "globalFilter",
  version: 1,
  storage: tabStorage,
  timeout: 0,
  blacklist: ["adminAreaSettingsDataTemp", "selectedAreaIdsTemp"],
  migrations: {
    /**
     * Version 1: removes linkedDatasetID from the globalFilter state. The dataset
     * state has no linkedDatasetID after its migration. Thus the field is not
     * necessary. This migration removes the field from each persisted state.
     * During the migration, localforage keeps the linkedDatasetID. The App
     * component uses that value to update the dataset state, and then removes it
     * from localforage.
     */
    1: async (state) => {
      const legacyState = state as
        | (GlobalFilterState & { linkedDatasetIds?: string[] })
        | undefined;

      if (!legacyState) return state;

      try {
        if (legacyState.linkedDatasetIds?.length) {
          await tabStorage.setItem(
            MIGRATION_KEYS.LINKED_DATASET_IDS,
            legacyState.linkedDatasetIds
          );
        }
      } catch (e) {
        console.error(
          "[globalFilter migration v1] Failed to save linkedDatasetIds:",
          e
        );
      }

      delete legacyState.linkedDatasetIds;
      return legacyState;
    }
  }
});

const widgetPersistConfig = createPersistConfig({
  key: "widget",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

// Use tabStorage and not sharedStorage. The parked snapshot must agree with the
// slices of its tab. The app persists the snapshot. Thus a page refresh in a
// session finds a state to restore.
const analysisConfigSnapshotPersistConfig = createPersistConfig({
  key: "analysisConfigSnapshot",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

// The same rule applies to the part of a parked workspace that only a tour keeps.
// `activeStepTourId` is in the blacklist. It is temporary tour UI data, and a
// rehydrated step id would mark a nav item for a step that is not on the screen.
const onboardingPersistConfig = createPersistConfig({
  key: "onboarding",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: ["activeStepTourId"]
});

export const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  dataset: persistReducer(datasetPersistConfig, datasetReducer),
  spatial: persistReducer(spatialPersistConfig, spatialReducer),
  ui: persistReducer(uiPersistConfig, uiReducer),
  analysisSummary: persistReducer(
    analysisSummaryPersistConfig,
    analysisSummaryReducer
  ),
  analysisJob: persistReducer(analysisJobPersistConfig, analysisJobReducer),
  globalFilter: persistReducer(globalFilterPersistConfig, globalFilterReducer),
  widget: persistReducer(widgetPersistConfig, widgetReducer),
  onboarding: persistReducer(onboardingPersistConfig, onboardingReducer),
  analysisConfigSnapshot: persistReducer(
    analysisConfigSnapshotPersistConfig,
    analysisConfigSnapshotReducer
  )
});

export const store = configureStore({
  devTools: import.meta.env.DEV,
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore the actions of redux-persist. They can contain values that are
        // not serializable, and the persistence needs them.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

// Sets the store to its initial state. Add or remove slices as necessary.
export const resetStore = async () => {
  store.dispatch(resetAnalysisSummaryState());
  store.dispatch(resetAuth());
  store.dispatch(resetDatasetState());
  store.dispatch(resetMapState());
  store.dispatch(resetSpatialState());
  store.dispatch(resetUIDataConfig());
  store.dispatch(resetAnalysisJob());
  store.dispatch(resetGlobalFilter());
  store.dispatch(resetWidgetState());
  store.dispatch(resetOnboardingState());
  store.dispatch(clearAnalysisConfigSnapshot());
};

/** Logout: sets the slices to their initial state, then clears the storage of the other tabs. */
export const logoutCleanupStore = async () => {
  resetStore();
  await clearOtherTabStorages();
};

// Get the `RootState` and the `AppDispatch` types from the store.
export type RootState = ReturnType<typeof store.getState>;
// Example of the type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
